import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { Toggle } from 'uikit/Toggle/Toggle';
import './SettingsTab.scss';

const SettingsTab = ({ isLoading, customerNumber, settings, getCustomerSettings, updateSetting }) => {
  const i18n = useContext(i18nContext);
  const [pendingSettings, setPendingSettings] = useState(new Set());

  useEffect(() => {
    getCustomerSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) {
    return <Loader className={'application-loader settings-tab-loader'} />;
  }

  // Unknown keys (added on backend before BO2 translations) fall back to the raw key
  const getMessageOrDefault = (id, defaultMessage) => {
    const message = i18n.getMessage(id);
    return message && message !== id ? message : defaultMessage;
  };

  const handleToggleSetting = async (settingKey, currentState) => {
    // Prevent multiple clicks on the same setting
    if (pendingSettings.has(settingKey)) {
      return;
    }

    setPendingSettings((prev) => new Set(prev).add(settingKey));

    try {
      await updateSetting(customerNumber, settingKey, !currentState);
    } finally {
      setPendingSettings((prev) => {
        const next = new Set(prev);
        next.delete(settingKey);
        return next;
      });
    }
  };

  return (
    <div className={'settings-tab-container'}>
      <div className={'settings-tab-header'}>
        <h3>{i18n.getMessage('settingsTab.title')}</h3>
        <p className={'settings-tab-description'}>{i18n.getMessage('settingsTab.description')}</p>
      </div>

      <h4 className={'settings-tab-subtitle'}>{i18n.getMessage('settingsTab.emailNotifications.title')}</h4>

      <Table responsive className={'settings-table'}>
        <thead>
          <tr>
            <th>{i18n.getMessage('settingsTab.table.setting')}</th>
            <th>{i18n.getMessage('settingsTab.table.description')}</th>
            <th>{i18n.getMessage('settingsTab.table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {settings.map((setting) => {
            const isEnabled = String(setting.value) === 'true';

            return (
              <tr key={setting.key} className={'settings-table-row'}>
                <td className={'settings-table-cell setting-name'}>
                  <strong>{getMessageOrDefault(`settingsTab.settings.${setting.key}.name`, setting.key)}</strong>
                </td>
                <td className={'settings-table-cell setting-description'}>
                  {getMessageOrDefault(`settingsTab.settings.${setting.key}.description`, '')}
                </td>
                <td className={'settings-table-cell setting-toggle'}>
                  <Toggle
                    isEnabled={isEnabled}
                    onChange={() => handleToggleSetting(setting.key, isEnabled)}
                    disabled={isLoading || pendingSettings.has(setting.key)}
                    label={
                      isEnabled ? i18n.getMessage('settingsTab.status.on') : i18n.getMessage('settingsTab.status.off')
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {settings.length === 0 && <div className={'settings-tab-empty'}>{i18n.getMessage('settingsTab.noSettings')}</div>}
    </div>
  );
};

SettingsTab.propTypes = {
  isLoading: PropTypes.bool,
  customerNumber: PropTypes.string.isRequired,
  settings: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  getCustomerSettings: PropTypes.func.isRequired,
  updateSetting: PropTypes.func.isRequired
};

export default inject((stores) => ({}))(observer(SettingsTab));
